// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md

import type {
  CredentialTestOutcome,
  IntegrationCredential,
  IntegrationListItem,
} from '@greenlight/shared';

import { authCategoryLabel } from '@/components/greenlight/AuthCategoryChip';
import { relativeTime } from '@/components/greenlight/relativeTime';
import type { StatusBadgeVariant } from '@/components/greenlight/StatusBadge';

export interface MetaSegment {
  text: string;
  /** Operational facts (last access, registered-by) recede so security facts read first. */
  receded: boolean;
}

export interface CredentialVM {
  slug: string;
  scope: string;
  configured: boolean;
  // Null for a secretless credential (workload_identity connected DB) — the
  // proxy authenticates as the platform data identity, nothing is vaulted.
  vaultKey: string | null;
  /** Storage guarantee — true for every configured credential, independent of read/write scope. */
  neverReadBack: boolean;
  /** Shown only when not configured. */
  notConfiguredLabel: string;
  appsCount: number;
  appsLabel: string;
  approvalMode: 'auto' | 'manual';
  deliveryMode: 'proxied' | 'injected';
  /** The last credential test IT ran on this saved credential, if any. */
  lastTest: { outcome: CredentialTestOutcome; tested_at: string; tested_by?: string | null } | null;
}

export interface IntegrationCardVM {
  id: string;
  slug: string;
  name: string;
  kind: string;
  /** Catalog entry key (ADR-0026); null for Custom. Drives the tile logo. */
  catalogKey: string | null;
  authMode: string;
  targetUrl: string | null;
  builtIn: boolean;
  postMvp: boolean;
  statusVariant: StatusBadgeVariant;
  meta: MetaSegment[];
  /** Why this integration cannot serve a call yet, when that is knowable from the row. */
  configurationWarning: string | null;
  leadDescription: string;
  credentials: CredentialVM[];
  grantsHref: string;
}

export function presentIntegrationTarget(item: IntegrationListItem): string | null {
  if (item.base_url) return item.base_url;
  if (!item.host) return null;
  const port = item.port != null ? `:${item.port}` : '';
  const database = item.database_name ? `/${item.database_name}` : '';
  return `${item.host}${port}${database}`;
}

export interface SummaryVM {
  integrationsLabel: string;
  connectedAppsLabel: string;
  note: string;
}

/**
 * Why a credential is unusable, when the reason is knowable from the row. A
 * bare "Not configured" sends IT looking for a missing secret; the secret is
 * usually present and the registration params are what is missing.
 */
export function notConfiguredReason(item: IntegrationListItem): string | null {
  if (item.catalog_key !== 'google-workspace' || item.auth_mode !== 'service_account') return null;
  const scopes = item.auth_config?.['scopes'];
  return Array.isArray(scopes) && scopes.length > 0
    ? null
    : 'Needs OAuth scopes — edit the integration to add them';
}

export function presentCredential(
  c: IntegrationCredential,
  postMvp: boolean,
  deliveryMode: 'proxied' | 'injected',
): CredentialVM {
  return {
    slug: c.slug,
    scope: c.scope,
    configured: c.configured,
    vaultKey: c.vault_key,
    neverReadBack: c.configured,
    notConfiguredLabel: postMvp ? 'Not configured · later release' : 'Not configured',
    appsCount: c.apps_count,
    appsLabel: `${c.apps_count} ${c.apps_count === 1 ? 'grant' : 'grants'}`,
    approvalMode: c.approval_mode,
    deliveryMode,
    lastTest: c.last_test ?? null,
  };
}

export function presentIntegrationCard(item: IntegrationListItem, now?: number): IntegrationCardVM {
  const deliveryText =
    item.delivery_mode === 'injected'
      ? item.env_var_name
        ? `Injected · ${item.env_var_name}`
        : 'Injected'
      : 'Proxied';

  const lastAccessText = item.last_access_at ? relativeTime(item.last_access_at, now) : 'never';

  const meta: MetaSegment[] = [
    { text: authCategoryLabel(item.auth_category), receded: false },
    { text: deliveryText, receded: false },
    {
      text: `${item.apps_count} granted`,
      receded: false,
    },
    { text: `Last access ${lastAccessText}`, receded: true },
    { text: `Registered by ${item.registered_by}`, receded: true },
  ];

  return {
    id: item.id,
    slug: item.slug,
    name: item.name,
    kind: item.kind,
    catalogKey: item.catalog_key,
    authMode: item.auth_mode,
    targetUrl: presentIntegrationTarget(item),
    builtIn: item.built_in,
    postMvp: item.post_mvp,
    statusVariant: item.status,
    meta,
    leadDescription: item.identity_note,
    credentials: item.credentials.map((c) =>
      presentCredential(c, item.post_mvp, item.delivery_mode),
    ),
    configurationWarning: notConfiguredReason(item),
    grantsHref: `/integrations?view=grants&group=integration&integrationId=${item.id}`,
  };
}

/**
 * `partial` marks the counts as lower bounds ("14+ integrations") because more
 * pages of the registry remain unloaded — they must never read as org totals.
 */
export function presentSummary(
  items: IntegrationListItem[],
  opts: { partial?: boolean } = {},
): SummaryVM {
  const connectedApps = items.reduce((sum, i) => sum + i.apps_count, 0);
  const partial = opts.partial === true;
  const count = (n: number, singular: string, plural: string): string =>
    `${n}${partial ? '+' : ''} ${n === 1 && !partial ? singular : plural}`;
  return {
    integrationsLabel: count(items.length, 'integration', 'integrations'),
    connectedAppsLabel: `${connectedApps}${partial ? '+' : ''} active ${
      connectedApps === 1 && !partial ? 'grant' : 'grants'
    }`,
    note: 'Credentials are write-only — stored in Key Vault, never read back.',
  };
}
